import type { ConditionCategory } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/conditions';
import type { TriggerDriverIdentificationRecurrence } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/driverIdentification';
import type { TriggerUserPreferences } from '@cobliteam/api-lib/src/dashboard/alerts/definitions/users';
import type { UUID } from '@cobliteam/common-types';

/**
 * The journeys that can suggest an alert. Each value maps (in `alertHookConfig`) either to a real
 * `RuleCategory` + default `ConditionCategory` of the alerts domain (`kind: 'trigger'`) or to a
 * blueprint-api hook template (`kind: 'blueprint'`).
 */
enum AlertHookType {
  SPEED = 'SPEED',
  IDENTIFICATION = 'IDENTIFICATION',
  CAMERA_OBSTRUCTED = 'CAMERA_OBSTRUCTED',
  CHECKLIST = 'CHECKLIST',
  GEOFENCE_INOUT = 'GEOFENCE_INOUT',
  IDLE_ENGINE = 'IDLE_ENGINE',
  CHECKLIST_NON_COMPLIANCE = 'CHECKLIST_NON_COMPLIANCE',
}

/** Step 1 pitches the alert; step 2 previews the pre-configured trigger and activates it. */
type AlertHookStep = 'suggestion' | 'confirm';

/**
 * Entity ids captured from the journey that triggered the hook. They scope the
 * trigger that will be created (see `buildPayload`). `CHECKLIST` and `CHECKLIST_NON_COMPLIANCE` are
 * fleet-wide, so they carry no entity.
 */
interface AlertHookParamsMap {
  [AlertHookType.SPEED]: { vehicleIds: UUID[] };
  [AlertHookType.IDENTIFICATION]: { vehicleIds?: UUID[]; vehicleGroupIds?: UUID[] };
  [AlertHookType.CAMERA_OBSTRUCTED]: { vehicleIds: UUID[] };
  [AlertHookType.CHECKLIST]: Record<string, never>;
  [AlertHookType.GEOFENCE_INOUT]: { geofenceIds: UUID[] };
  [AlertHookType.IDLE_ENGINE]: { vehicleIds?: UUID[] };
  [AlertHookType.CHECKLIST_NON_COMPLIANCE]: Record<string, never>;
}

type AlertHookParams<T extends AlertHookType = AlertHookType> = AlertHookParamsMap[T];

/**
 * Values collected by the confirm-step mini-form (Task AE-2326). `conditionCategory`
 * starts from `conditionCategoryDefault` and may be changed by the user (speed window,
 * idle duration, geofence direction). Resolved to a `conditionId` via `resolveConditionId`.
 */
interface AlertHookFormValues {
  name: string;
  conditionCategory: ConditionCategory;
  allowNotifications: boolean;
  notifiedUsersChannels: TriggerUserPreferences[];
  driverIdentificationWithoutDriverRecurrence?: TriggerDriverIdentificationRecurrence;
}

interface CurrentAlertHook {
  type: AlertHookType;
  params: AlertHookParams;
  step: AlertHookStep;
  /**
   * Called once when the modal leaves the screen (close or dismiss), whatever the outcome.
   * Lets the journey resume its own flow (e.g. GeofenceForm navigating to the geofences list)
   * only after the user is done with the suggestion. Ephemeral — never persisted.
   */
  onClosed?: () => void;
}

export type { AlertHookFormValues, AlertHookParams, AlertHookParamsMap, AlertHookStep, CurrentAlertHook };
export { AlertHookType };
